import { analyzeLoading, analyzeLoadingDone, currentUser, analyzeStadgarLoading, analyzeStadgarLoadingDone, 
         analyzeEnergyLoading, analyzeEnergyLoadingDone, createRedovisningExpertLoading, createRedovisningExpertLoadingDone,
         analyzeFinancialPlanLoading, analyzeFinancialPlanLoadingDone, selectedItem } from '../store/store.js';
import { fetchBrf } from './utils.js';
import { rateBrf } from '../components/Rating/Rating.svelte';
import { get } from 'svelte/store';

export async function handleAnalyzeBrf(fileName, coreId){ 
    analyzeLoading.set(true);
    analyzeLoadingDone.set(false);
    let url = 'https://boanalys-backend-324523837899.europe-north1.run.app/analyze_document'

    const queryParams = new URLSearchParams({ file_name: fileName });
    if (coreId) {
        queryParams.append('core_id', coreId);
    }

    const user = get(currentUser);
    
    const maxRetries = 3;
    let lastError;
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
            const response = await fetch(`${url}?${queryParams.toString()}`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user?.accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to trigger backend function: ${response.status}`);
            }

            const data = await response.json();
            const { core_id } = data;

            analyzeLoading.set(false);
            analyzeLoadingDone.set(true);

            fetchBrf(core_id); 
            await rateBrf(core_id, null, user);
            fetchBrf(core_id); 

            return core_id;
        } catch (error) {
            lastError = error;
            if (attempt < maxRetries - 1) {
                await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
            }
        }
    }
    
    analyzeLoading.set(false);
    throw lastError;
}

export async function handleCreateBrfExpert(fileName, coreId) {
    console.log(`Attempting to create expert for:`, fileName, "CoreID:", coreId);

    let url = 'https://brf-analyze-backend-tnkskvn76a-lz.a.run.app/extract_text_from_file_name';
    const queryParams = new URLSearchParams();
    // Use the specific parameter name for the file based on type if needed, or a generic one
    queryParams.append('file_name', fileName); // Generic, backend can differentiate if needed

    if (coreId) {
        queryParams.append('core_id', coreId);
    }

    const storeState = get(currentUser);
    const response = await fetch(`${url}?${queryParams.toString()}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${storeState?.accessToken}`,
        },
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error(`Failed to create redovisning expert. Status: ${response.status}, Message: ${errorText}`);
        throw new Error(`Failed to create redovisning expert`);
    }

    const data = await response.json();

    createRedovisningExpertLoading.set(true);
    createRedovisningExpertLoadingDone.set(false);
}

export async function analyzeUploadedStadgar(fileName, coreId) {
    analyzeStadgarLoading.set(true);
    analyzeStadgarLoadingDone.set(false);
    let url = 'https://brf-analyze-backend-tnkskvn76a-lz.a.run.app/analyze_stadgar_structured_initialized';

    const queryParams = new URLSearchParams({ stadgar_file_name: fileName });
    if (coreId) {
        queryParams.append('core_id', coreId);
    }

    const storeState = get(currentUser);

    const response = await fetch(`${url}?${queryParams.toString()}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${storeState?.accessToken}`,
        },
    });

    if (!response.ok) {
        analyzeStadgarLoading.set(false);
        throw new Error('Failed to trigger stadgar analysis backend function');
    }

    const data = await response.json();
    analyzeStadgarLoading.set(false);
    analyzeStadgarLoadingDone.set(true);
    fetchBrf(coreId);
    return data;
}

export async function analyzeUploadedEnergy(fileName, coreId) {
    analyzeEnergyLoading.set(true);
    analyzeEnergyLoadingDone.set(false);
    let url = 'https://boanalys-backend-324523837899.europe-north1.run.app/analyze_energy_declaration';

    const queryParams = new URLSearchParams({ energy_file_name: fileName });
    if (coreId) {
        queryParams.append('core_id', coreId);
    }
    
    const selectedItemState = get(selectedItem);
    if (selectedItemState?.org_number) {
        queryParams.append('organisation_number', selectedItemState.org_number);
    }

    const storeState = get(currentUser);

    const response = await fetch(`${url}?${queryParams.toString()}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${storeState?.accessToken}`,
        },
    });

    if (!response.ok) {
        analyzeEnergyLoading.set(false);
        const errorText = await response.text();
        console.error(`Failed to trigger energy analysis. Status: ${response.status}, Message: ${errorText}`);
        throw new Error('Failed to trigger energy analysis backend function');
    }

    const data = await response.json();
    analyzeEnergyLoading.set(false);
    analyzeEnergyLoadingDone.set(true);
    fetchBrf(coreId);
    return data;
}

export async function analyzeUploadedFinancialPlan(fileName, coreId) {
    analyzeFinancialPlanLoading.set(true);
    analyzeFinancialPlanLoadingDone.set(false);
    let url = 'https://boanalys-backend-324523837899.europe-north1.run.app/analyze_financial_plan';

    const queryParams = new URLSearchParams({ file_name: fileName });
    if (coreId) {
        queryParams.append('core_id', coreId);
    }

    // Get the current value of the store
    const user = get(currentUser);
    
    const response = await fetch(`${url}?${queryParams.toString()}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${user?.accessToken}`,
        },
    });

    if (!response.ok) {
        throw new Error('Failed to trigger backend function');
    }

    const data = await response.json();

    if (!data.core_id) {
        throw new Error('No core_id returned from financial plan analysis');
    }

    const { core_id } = data;

    analyzeFinancialPlanLoading.set(false);
    analyzeFinancialPlanLoadingDone.set(true);

    fetchBrf(core_id);
    return data;
}